import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiCalendar, FiTrendingUp, FiClock, FiTarget, FiZap, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getCaliperMetrics, getAssessmentResults, UserMetrics, CaliperMetricsResponse, AssessmentResultsResponse } from '../../api/admin';
import { TRACK_NAMES } from '../../types/constants';
import { includesAugust2025 } from '../../utils/dateRanges';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';

// Helper function to convert Date to local YYYY-MM-DD string
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const ProgressDashboardHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Progress Dashboard
    </span>
  </h1>
);

// Date range selector component
interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onSearch: () => void;
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false
}) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onSearch}
              disabled={isLoading || !startDate || !endDate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              <FiCalendar className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button
              onClick={onRefresh}
              disabled={isLoading || !startDate || !endDate}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress data interface
interface ProgressData {
  userEmail: string;
  cqpmIncreasePerHour: number;
  masteredUnitsPerHour: number;
  efficiency: number;
  activeTimeHours: number;
  wasteTimeHours: number;
  totalMasteredUnits: number;
  cqpmIncrease: number;
  trackId?: string; // Optional for track-specific data
}

// Summary stats component
interface SummaryStatsProps {
  avgCqpmIncreasePerHour: number;
  avgMasteredUnitsPerHour: number;
  avgEfficiency: number;
  totalUsers: number;
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  avgCqpmIncreasePerHour,
  avgMasteredUnitsPerHour,
  avgEfficiency,
  totalUsers,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-8 bg-gray-300 rounded mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Users',
      value: totalUsers.toLocaleString(),
      icon: FiTarget,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Avg CQPM Increase/Hour',
      value: avgCqpmIncreasePerHour.toFixed(2),
      icon: FiTrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Avg Units Mastered/Hour',
      value: avgMasteredUnitsPerHour.toFixed(2),
      icon: FiZap,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Avg Efficiency',
      value: `${(avgEfficiency * 100).toFixed(1)}%`,
      icon: FiClock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {stats.map((stat, index) => (
        <Card key={index} className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Progress table component
interface ProgressTableProps {
  data: ProgressData[];
  dateRange: { startDate: string; endDate: string };
  isLoading?: boolean;
}

const ProgressTable: React.FC<ProgressTableProps> = ({
  data,
  dateRange,
  isLoading = false
}) => {
  const showTrackColumn = !includesAugust2025(dateRange.startDate, dateRange.endDate);
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            No progress data found for the selected date range.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                {showTrackColumn && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Track Name
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CQPM Increase/Hour
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mastered Units/Hour
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Efficiency
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Time (hrs)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((user, index) => (
                <tr key={`${user.userEmail}-${user.trackId || 'legacy'}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.userEmail}
                  </td>
                  {showTrackColumn && (
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.trackId ? (TRACK_NAMES[user.trackId as keyof typeof TRACK_NAMES] || user.trackId) : 'Multiple'}
                    </td>
                  )}
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                    {user.cqpmIncreasePerHour.toFixed(2)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                    {user.masteredUnitsPerHour.toFixed(2)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.efficiency >= 0.8 
                        ? 'bg-green-100 text-green-800' 
                        : user.efficiency >= 0.6 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {(user.efficiency * 100).toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.activeTimeHours.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const ProgressDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // State
  const [startDate, setStartDate] = useState(() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 2);
    return formatDateLocal(startDate);
  });
  const [endDate, setEndDate] = useState(() => formatDateLocal(new Date()));
  const [caliperData, setCaliperData] = useState<CaliperMetricsResponse | null>(null);
  const [assessmentData, setAssessmentData] = useState<AssessmentResultsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate progress data from combined sources
  const progressData = useMemo(() => {
    if (!caliperData?.users || !assessmentData?.assessments) {
      return [];
    }

    const useTrackSpecific = !includesAugust2025(startDate, endDate);
    const data: ProgressData[] = [];

    // Create assessment maps for performance optimization (O(U + A) instead of O(U×A))
    const trackSpecificAssessments = new Map<string, any[]>();
    const userAssessments = new Map<string, any[]>();

    // Populate maps once by iterating through all assessments
    assessmentData.assessments.forEach(assessment => {
      // For track-specific mode: key = userEmail|trackId
      const trackSpecificKey = `${assessment.userEmail}|${assessment.trackId}`;
      if (!trackSpecificAssessments.has(trackSpecificKey)) {
        trackSpecificAssessments.set(trackSpecificKey, []);
      }
      trackSpecificAssessments.get(trackSpecificKey)!.push(assessment);

      // For legacy mode: key = userEmail
      const userKey = assessment.userEmail;
      if (!userAssessments.has(userKey)) {
        userAssessments.set(userKey, []);
      }
      userAssessments.get(userKey)!.push(assessment);
    });

    if (useTrackSpecific) {
      // Track-specific mode: Create separate entries for each user-track combination

      caliperData.users.forEach(userCaliper => {
        // Skip if this user doesn't have trackId (shouldn't happen in Sept+ data)
        if (!userCaliper.trackId) return;

        // Find user's assessments for this specific track using Map lookup
        const trackSpecificKey = `${userCaliper.userEmail}|${userCaliper.trackId}`;
        const trackAssessments = trackSpecificAssessments.get(trackSpecificKey) || [];


        // Skip if no assessments for this track
        if (trackAssessments.length === 0) return;

        // Group assessments by date and take max score per day
        const dailyMaxScores = new Map<string, number>();
        trackAssessments.forEach(assessment => {
          // Extract just the date part (YYYY-MM-DD) from ISO timestamp
          const date = assessment.date.split('T')[0];
          const currentMax = dailyMaxScores.get(date) || 0;
          dailyMaxScores.set(date, Math.max(currentMax, assessment.speedScore));
        });

        // Calculate CQPM increase using daily max scores
        let trackCqpmIncrease = 0;
        const dailyScores = Array.from(dailyMaxScores.values());
        if (dailyScores.length > 1) {
          const rawIncrease = Math.max(...dailyScores) - Math.min(...dailyScores);
          const numDays = dailyScores.length;
          const avgIncreasePerDay = rawIncrease / numDays;

          // If average CQPM improvement per day > 5, count as 0
          trackCqpmIncrease = avgIncreasePerDay > 5 ? 0 : rawIncrease;
        }


        // Convert times to hours (userCaliper already has track-specific time data)
        const activeTimeHours = userCaliper.activeTime / 3600;
        const wasteTimeHours = userCaliper.wasteTime / 3600;

        // Calculate track-specific metrics
        const cqpmIncreasePerHour = activeTimeHours > 0 ? trackCqpmIncrease / activeTimeHours : 0;
        const masteredUnitsPerHour = activeTimeHours > 0 ? userCaliper.masteredUnits / activeTimeHours : 0;
        const totalTime = activeTimeHours + wasteTimeHours;
        const efficiency = totalTime > 0 ? (1 - (wasteTimeHours / totalTime)) : 0;


        data.push({
          userEmail: userCaliper.userEmail,
          trackId: userCaliper.trackId,
          cqpmIncreasePerHour,
          masteredUnitsPerHour,
          efficiency,
          activeTimeHours,
          wasteTimeHours,
          totalMasteredUnits: userCaliper.masteredUnits,
          cqpmIncrease: trackCqpmIncrease
        });
      });
    } else {
      // Legacy mode: Aggregate across all tracks per user (old logic)
      caliperData.users.forEach(user => {
        // Find user's assessments across all tracks using Map lookup
        const userAssessmentsForUser = userAssessments.get(user.userEmail) || [];

        if (userAssessmentsForUser.length === 0) return;

        // Calculate CQPM increase per track using daily max scores, then sum them
        const trackIncreases = new Map<string, Map<string, number>>();

        // Group scores by track and date, keeping max score per day per track
        userAssessmentsForUser.forEach(assessment => {
          if (!trackIncreases.has(assessment.trackId)) {
            trackIncreases.set(assessment.trackId, new Map<string, number>());
          }
          const trackMap = trackIncreases.get(assessment.trackId)!;
          // Extract just the date part (YYYY-MM-DD) from ISO timestamp
          const date = assessment.date.split('T')[0];
          const currentMax = trackMap.get(date) || 0;
          trackMap.set(date, Math.max(currentMax, assessment.speedScore));
        });

        // Calculate increase per track using daily max scores and sum them
        let totalCqpmIncrease = 0;
        trackIncreases.forEach((dailyScoresMap, trackId) => {
          const dates = Array.from(dailyScoresMap.keys()).sort();
          if (dates.length > 1) {
            const firstDate = dates[0];
            const lastDate = dates[dates.length - 1];
            const startScore = dailyScoresMap.get(firstDate)!;
            const endScore = dailyScoresMap.get(lastDate)!;
            const rawTrackIncrease = Math.max(0, endScore - startScore);
            const numDays = dates.length;
            const avgIncreasePerDay = rawTrackIncrease / numDays;

            // If average CQPM improvement per day > 5, count as 0
            const trackIncrease = avgIncreasePerDay > 5 ? 0 : rawTrackIncrease;
            totalCqpmIncrease += trackIncrease;
          }
        });

        // Convert times to hours
        const activeTimeHours = user.activeTime / 3600;
        const wasteTimeHours = user.wasteTime / 3600;

        // Calculate metrics
        const cqpmIncreasePerHour = activeTimeHours > 0 ? totalCqpmIncrease / activeTimeHours : 0;

        const masteredUnitsPerHour = activeTimeHours > 0 ? user.masteredUnits / activeTimeHours : 0;
        const totalTime = activeTimeHours + wasteTimeHours;
        const efficiency = totalTime > 0 ? (1 - (wasteTimeHours / totalTime)) : 0;

        data.push({
          userEmail: user.userEmail,
          cqpmIncreasePerHour,
          masteredUnitsPerHour,
          efficiency,
          activeTimeHours,
          wasteTimeHours,
          totalMasteredUnits: user.masteredUnits,
          cqpmIncrease: totalCqpmIncrease
          // No trackId for legacy mode
        });
      });
    }

    return data;
  }, [caliperData, assessmentData]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (progressData.length === 0) {
      return {
        avgCqpmIncreasePerHour: 0,
        avgMasteredUnitsPerHour: 0,
        avgEfficiency: 0,
        totalUsers: 0
      };
    }


    // Filter out values > 50 for summary calculation only
    const filteredData = progressData.filter(user => user.cqpmIncreasePerHour <= 50);
    const avgCqpmIncreasePerHour = filteredData.length > 0
      ? filteredData.reduce((sum, user) => sum + user.cqpmIncreasePerHour, 0) / filteredData.length
      : 0;

    // Filter out values > 50 for Avg Units Mastered/Hour as well
    const filteredDataUnits = progressData.filter(user => user.masteredUnitsPerHour <= 50);
    const avgMasteredUnitsPerHour = filteredDataUnits.length > 0
      ? filteredDataUnits.reduce((sum, user) => sum + user.masteredUnitsPerHour, 0) / filteredDataUnits.length
      : 0;
    const avgEfficiency = progressData.reduce((sum, user) => sum + user.efficiency, 0) / progressData.length;

    return {
      avgCqpmIncreasePerHour,
      avgMasteredUnitsPerHour,
      avgEfficiency,
      totalUsers: progressData.length
    };
  }, [progressData]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [caliperResult, assessmentResult] = await Promise.all([
        getCaliperMetrics(startDate, endDate),
        getAssessmentResults(startDate, endDate)
      ]);
      
      setCaliperData(caliperResult);
      setAssessmentData(assessmentResult);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch progress data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!progressData || progressData.length === 0) return;

    const showTrackColumn = !includesAugust2025(startDate, endDate);
    
    // Define CSV headers based on table columns
    const headers = [
      { key: 'userEmail' as keyof ProgressData, label: 'User Email' },
      ...(showTrackColumn ? [{ key: 'trackId' as keyof ProgressData, label: 'Track Name' }] : []),
      { key: 'cqpmIncreasePerHour' as keyof ProgressData, label: 'CQPM Increase/Hour' },
      { key: 'masteredUnitsPerHour' as keyof ProgressData, label: 'Mastered Units/Hour' },
      { key: 'efficiency' as keyof ProgressData, label: 'Efficiency (%)' },
      { key: 'activeTimeHours' as keyof ProgressData, label: 'Active Time (hours)' }
    ];

    // Transform data for CSV with formatted values
    const csvData = progressData.map(user => ({
      ...user,
      trackId: user.trackId ? (TRACK_NAMES[user.trackId as keyof typeof TRACK_NAMES] || user.trackId) : 'Multiple',
      efficiency: (user.efficiency * 100).toFixed(1)
    }));

    const csvContent = convertToCSV(csvData, headers);
    const filename = `progress-dashboard-${startDate}-to-${endDate}-${formatDateForFilename()}.csv`;
    
    downloadCSV(csvContent, filename);
  }, [progressData, startDate, endDate]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <ProgressDashboardHeader />
        
        {/* Navigation */}
        <div className="flex mb-4">
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Date Range Selector */}
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={progressData.length > 0}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              avgCqpmIncreasePerHour={summaryStats.avgCqpmIncreasePerHour}
              avgMasteredUnitsPerHour={summaryStats.avgMasteredUnitsPerHour}
              avgEfficiency={summaryStats.avgEfficiency}
              totalUsers={summaryStats.totalUsers}
              isLoading={isLoading}
            />

            {/* Progress Table */}
            <ProgressTable
              data={progressData}
              dateRange={{ startDate, endDate }}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};